import { InternalServerError } from './errors.mjs';

class Bad {
    value;
    constructor(value) {
        this.value = value;
    }
    isBad() {
        return true;
    }
    isGood() {
        return false;
    }
    getOrElse(defaultValue) {
        return defaultValue;
    }
    good() {
        throw this.value;
    }
    match(onGood, onBad) {
        return onBad(this.value);
    }
    map(_) {
        return this;
    }
    flatMap(_) {
        return this;
    }
    badMap(f) {
        return Bad.of(f(this.value));
    }
    mapAsync(_) {
        return new PromiseResult(Promise.resolve(this));
    }
    flatMapAsync(_) {
        return new PromiseResult(Promise.resolve(this));
    }
    badMapAsync(f) {
        return new PromiseResult(f(this.value).then((newError) => Bad.of(newError)));
    }
    recover(f) {
        return Good.of(f(this.value));
    }
    recoverAsync(f) {
        return new PromiseResult(f(this.value).then((value) => Good.of(value)));
    }
    tap(_) {
        return this;
    }
    tapAsync(_) {
        return new PromiseResult(Promise.resolve(this));
    }
    tapError(f) {
        f(this.value);
        return this;
    }
    tapErrorAsync(f) {
        return new PromiseResult(f(this.value).then(() => this));
    }
    *[Symbol.iterator]() {
        return (yield this);
    }
    static of(e) {
        return new Bad(e);
    }
}
class Good {
    value;
    constructor(value) {
        this.value = value;
    }
    isBad() {
        return false;
    }
    isGood() {
        return true;
    }
    getOrElse(_defaultValue) {
        return this.value;
    }
    good() {
        return this.value;
    }
    match(onGood, _onBad) {
        return onGood(this.value);
    }
    map(f) {
        return Good.of(f(this.value));
    }
    flatMap(f) {
        return f(this.value);
    }
    badMap(_) {
        return this;
    }
    mapAsync(f) {
        return new PromiseResult(f(this.value).then((value) => Good.of(value)));
    }
    flatMapAsync(f) {
        return new PromiseResult(f(this.value));
    }
    badMapAsync(_) {
        return new PromiseResult(Promise.resolve(this));
    }
    recover(_) {
        return this;
    }
    recoverAsync(_) {
        return new PromiseResult(Promise.resolve(this));
    }
    tap(f) {
        f(this.value);
        return this;
    }
    tapAsync(f) {
        return new PromiseResult(f(this.value).then(() => this));
    }
    tapError(_) {
        return this;
    }
    tapErrorAsync(_) {
        return new PromiseResult(Promise.resolve(this));
    }
    *[Symbol.iterator]() {
        return (yield this);
    }
    static of(a) {
        return new Good(a);
    }
}
var Result;
(function (Result) {
    Result.ok = (value) => {
        return Good.of(value);
    };
    Result.err = (error) => {
        return Bad.of(error);
    };
    Result.fromPromise = (p) => {
        return new PromiseResult(p);
    };
    Result.okAsync = (value) => {
        return new PromiseResult(Promise.resolve(Good.of(value)));
    };
    Result.errAsync = (error) => {
        return new PromiseResult(Promise.resolve(Bad.of(error)));
    };
    function gen(f, thisArg) {
        const iter = f.call(thisArg);
        let state = iter.next();
        while (!state.done) {
            const result = state.value;
            if (result.isBad()) {
                iter.return(undefined);
                return result;
            }
            state = iter.next(result.value);
        }
        return Good.of(state.value);
    }
    Result.gen = gen;
    function genAsync(f, thisArg) {
        return new PromiseResult((async () => {
            const iter = f.call(thisArg);
            let state = await iter.next();
            while (!state.done) {
                const result = state.value;
                if (result.isBad()) {
                    await iter.return(undefined);
                    return result;
                }
                state = await iter.next(result.value);
            }
            return Good.of(state.value);
        })());
    }
    Result.genAsync = genAsync;
    Result.doWith = (results, cb) => {
        const ctx = results;
        for (const key in results) {
            if (results[key].isBad()) {
                return results[key];
            }
            ctx[key] = results[key].value;
        }
        return cb(ctx);
    };
    Result.doWithAsync = async (results, cb) => {
        const ctx = results;
        for (const key in results) {
            if (results[key].isBad()) {
                return results[key];
            }
            ctx[key] = results[key].value;
        }
        return (await cb(ctx));
    };
    Result.sequence = (results) => {
        const goodValues = [];
        for (const result of results) {
            if (result.isBad()) {
                return result;
            }
            goodValues.push(result.value);
        }
        return Good.of(goodValues);
    };
    Result.sequenceAsync = async (results) => {
        const goodValues = [];
        for (const result of results) {
            const unwrappedResult = await result;
            if (unwrappedResult.isBad()) {
                return unwrappedResult;
            }
            goodValues.push(unwrappedResult.value);
        }
        return Good.of(goodValues);
    };
    Result.fromTry = (tryFn, catchFn) => {
        try {
            return Good.of(tryFn());
        }
        catch (error) {
            return Bad.of(catchFn(error));
        }
    };
    Result.fromTryAsync = async (tryFn, catchFn) => {
        try {
            return Good.of(await tryFn());
        }
        catch (error) {
            return Bad.of(catchFn(error));
        }
    };
    function fromOption(valOpt, error) {
        if (valOpt !== null && valOpt !== undefined)
            return Good.of(valOpt);
        return Bad.of(error ?? new InternalServerError("Option is none"));
    }
    Result.fromOption = fromOption;
    Result.ensure = (condition, onFail) => (condition ? Good.of(undefined) : Bad.of(onFail()));
    Result.extractFromOption = (valOptResult) => {
        if (valOptResult === null || valOptResult === undefined)
            return Good.of(undefined);
        return valOptResult;
    };
    Result.partition = (results) => {
        const successes = [];
        const failures = [];
        for (const result of results) {
            if (result.isGood()) {
                successes.push(result.value);
            }
            else {
                failures.push(result.value);
            }
        }
        return { successes, failures };
    };
})(Result || (Result = {}));
class PromiseResult {
    promise;
    constructor(promise) {
        this.promise = promise;
    }
    static all(promises) {
        return new PromiseResult(Promise.all(promises).then((results) => {
            const values = [];
            for (const result of results) {
                if (result.isBad())
                    return result;
                values.push(result.value);
            }
            return Result.ok(values);
        }));
    }
    map(f) {
        return new PromiseResult(this.promise.then((r) => r.map(f)));
    }
    mapAsync(f) {
        return new PromiseResult(this.promise.then((r) => r.mapAsync(f)));
    }
    flatMap(f) {
        return new PromiseResult(this.promise.then((r) => r.flatMap(f)));
    }
    flatMapAsync(f) {
        return new PromiseResult(this.promise.then((r) => r.flatMapAsync(f)));
    }
    badMap(f) {
        return new PromiseResult(this.promise.then((r) => r.badMap(f)));
    }
    badMapAsync(f) {
        return new PromiseResult(this.promise.then((r) => r.badMapAsync(f)));
    }
    tap(f) {
        return new PromiseResult(this.promise.then((r) => r.tap(f)));
    }
    tapAsync(f) {
        return new PromiseResult(this.promise.then((r) => r.tapAsync(f)));
    }
    tapError(f) {
        return new PromiseResult(this.promise.then((r) => r.tapError(f)));
    }
    tapErrorAsync(f) {
        return new PromiseResult(this.promise.then((r) => r.tapErrorAsync(f)));
    }
    recover(f) {
        return new PromiseResult(this.promise.then((r) => r.recover(f)));
    }
    recoverAsync(f) {
        return new PromiseResult(this.promise.then((r) => r.recoverAsync(f)));
    }
    async match(onGood, onBad) {
        const result = await this.promise;
        return result.match(onGood, onBad);
    }
    async getOrElse(defaultValue) {
        return (await this.promise).getOrElse(defaultValue);
    }
    then(onfulfilled, onrejected) {
        return this.promise.then(onfulfilled, onrejected);
    }
    async *[Symbol.asyncIterator]() {
        const result = await this.promise;
        if (result.isBad()) {
            yield result;
            throw new Error("PromiseResult: yielded Bad result was not handled by genAsync");
        }
        return result.value;
    }
}

export { Bad, Good, PromiseResult, Result };
